import React from "react";
import { useNavigate } from "react-router-dom";
import { css } from "@emotion/css";
import { heroImage } from "../assets/images";

const Hero = () => {
    const navigate = useNavigate();

    const handleParticipantClick = () => {
        navigate("/registration", { state: { type: "participant" } });
    };

    const handleVolunteerClick = () => {
        navigate("/registration", { state: { type: "volunteer" } });
    };

    const handleKeyDown = (event, action) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            action();
        }
    };

    const heroStyles = {
        section: css`
            background: white;
            position: relative;
            overflow: hidden;
            padding: 0 36px;

            @media (min-width: 640px) {
                padding: 0 20px;
            }

            @media (min-width: 1024px) {
                padding: 0 25px;
            }
        `,
        container: css`
            width: 100%;
        `,
        grid: css`
            display: grid;
            grid-template-columns: repeat(1, 1fr);
            gap: 0;
            row-gap: 20px;
            align-items: stretch;
            min-height: 35px;

            @media (min-width: 640px) {
                min-height: 300px;
            }

            @media (min-width: 1024px) {
                grid-template-columns: repeat(2, 1fr);
                min-height: 400px;
            }
        `,
        imageContainer: css`
            position: relative;
            height: 100%;
        `,
        heroImage: css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: 8px;

            @media (min-width: 640px) {
                border-radius: 12px;
            }

            @media (min-width: 1024px) {
                border-top-left-radius: 16px;
                border-bottom-left-radius: 16px;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }
        `,
        textPanel: css`
            background-color: #fcac15;
            color: white;
            padding: 20px;
            display: flex;
            flex-direction: column;
            justify-content: center;
            height: 100%;
            border-radius: 8px;

            @media (min-width: 640px) {
                padding: 24px;
                border-radius: 12px;
            }

            @media (min-width: 1024px) {
                padding: 24px;
                border-radius: 0;
            }

            @media (max-width: 768px) {
                text-align: justify;
            }
        `,
        contentWrapper: css`
            animation: slide-up 0.6s ease-out;
        `,
        title: css`
            margin-bottom: 16px;
            line-height: 1.3;
            font-size: 28px;
            font-family: Marcellus;
            text-align: center;

            @media (min-width: 640px) {
                font-size: 32px;
                margin-bottom: 18px;
                text-align: left;
            }

            @media (min-width: 1024px) {
                font-size: 36px;
                margin-bottom: 20px;
            }
        `,
        subtitle: css`
            font-size: 16px;
            margin-bottom: 20px;
            color: white;
            line-height: 1.4;
            text-align: center;

            @media (min-width: 640px) {
                font-size: 18px;
                margin-bottom: 24px;
                text-align: left;
            }

            @media (min-width: 1024px) {
                font-size: 20px;
                margin-bottom: 28px;
            }
        `,
        buttonContainer: css`
            display: flex;
            flex-direction: column;
            gap: 12px;

            @media (min-width: 640px) {
                flex-direction: row;
                gap: 16px;
            }
        `,
        participantButton: css`
            background-color: #fc3c3c;
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            transition: all 0.2s;
            transform: scale(1);
            cursor: pointer;
            border: none;
            text-align: center;

            @media (min-width: 640px) {
                padding: 14px 24px;
                font-size: 15px;
                text-align: left;
            }

            @media (min-width: 1024px) {
                padding: 16px 28px;
                font-size: 16px;
            }

            &:hover {
                background-color: #b91c1c;
                transform: scale(1.05);
            }

            &:focus {
                outline: 3px solid #ffffff;
                outline-offset: 2px;
            }
        `,
        volunteerButton: css`
            background-color: #e59909;
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            transition: all 0.2s;
            transform: scale(1);
            cursor: pointer;
            border: none;
            text-align: center;

            @media (min-width: 640px) {
                padding: 14px 24px;
                font-size: 15px;
                text-align: left;
            }

            @media (min-width: 1024px) {
                padding: 16px 28px;
                font-size: 16px;
            }

            &:hover {
                background-color: #b45309;
                transform: scale(1.05);
            }

            &:focus {
                outline: 3px solid #ffffff;
                outline-offset: 2px;
            }
        `,
    };

    return (
        <section className={heroStyles.section} aria-labelledby="hero-title">
            <div className={heroStyles.container}>
                <div className={heroStyles.grid}>
                    {/* Left Section - Hero Image */}
                    <div className={heroStyles.imageContainer}>
                        <img
                            src={heroImage}
                            alt="Club Mango Friends Hero Image - Young people celebrating together in a joyful outdoor setting, representing the community spirit of Club Mango"
                            className={heroStyles.heroImage}
                        />
                    </div>

                    {/* Right Section - Text and Buttons */}
                    <div className={heroStyles.textPanel}>
                        <div className={heroStyles.contentWrapper}>
                            <h1 id="hero-title" className={heroStyles.title}>
                                Celebration of Oneness with Shreekalp
                            </h1>
                            <p className={heroStyles.subtitle}>
                                Have fun beyond Zero State
                            </p>

                            {/* Buttons */}
                            <div
                                className={heroStyles.buttonContainer}
                                role="group"
                                aria-label="Registration options"
                            >
                                <button
                                    className={heroStyles.participantButton}
                                    onClick={handleParticipantClick}
                                    onKeyDown={(e) =>
                                        handleKeyDown(e, handleParticipantClick)
                                    }
                                    aria-label="Join Club Mango as a participant - Navigate to registration form"
                                >
                                    Join as a participant
                                </button>
                                <button
                                    className={heroStyles.volunteerButton}
                                    onClick={handleVolunteerClick}
                                    onKeyDown={(e) =>
                                        handleKeyDown(e, handleVolunteerClick)
                                    }
                                    aria-label="Join Club Mango as a volunteer - Navigate to registration form"
                                >
                                    Join as a volunteer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
